import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

interface FormData {
  id: string | undefined;
  cityId: string;
  countryId: number;
  country: string;
  address: string;
  address2: string;
}

interface City {
  id: number;
  name: string;
}

interface Country {
  id: number;
  name: string;
}

export const EditUserAddress: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('userAddresses');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState<FormData>({
    id: id,
    cityId: "",
    countryId: 0,
    country: "",
    address: "",
    address2: "",
  });
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'countryId') {
      fetchCities(value);
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const fetchCities = async (countryId: string) => {
    try {
      const response = await adminApi.call(`/city/listofcityincountry?countryId=${countryId}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || "Error fetching cities");
        return;
      }

      setCities(response.data?.items || []);
    } catch (error: any) {
      showErrorToast("Error: " + error.message);
    }
  };
  useEffect(() => {
    const fetchUserAddress = async () => {
      try {
        const response = await adminApi.call(`/useraddress/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || "Failed to fetch address");
          return;
        }

        const data = response.data;
        const res = {
          id: id,
          cityId: data.cityId,
          countryId: data.city?.countryId,
          country: data.city?.country?.name,
          address: data.address,
          address2: data.address2,
        };
        setFormData(res);
        fetchCities(res.countryId);
      } catch (error: any) {
        showErrorToast(t('messages.loadingError') + ": " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAddress();
  }, [id, token]);

  useEffect(() => {
    const fetchInitialCities = async () => {
      try {
        const response = await adminApi.call('/city/listofcitiesinusercountry', 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('messages.loadingError'));
          return;
        }

        setCities(response.data?.items || []);
      } catch (error: any) {
        showErrorToast(t('messages.loadingError') + ": " + error.message);
      }
    };

    fetchInitialCities();
  }, [token]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await adminApi.call('/country', 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('messages.loadingError'));
          return;
        }

        setCountries(
          response.data?.items.filter((x: any) => (x.countryType === 1 || x.countryType === 0) && !x.isSuspended) || []
        );
      } catch (error: any) {
        showErrorToast(t('messages.loadingError') + ": " + error.message);
      }
    };

    fetchCountries();
  }, [token]);
  const submitItem = async () => {
    setLoading(true);

    try {
      const response = await adminApi.call('/userAddress', 'PUT', formData);

      if (!response.success) {
        showErrorToast(response.error || t('messages.updateError'));
        return;
      }

      showSuccessToast(t('messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('messages.updateError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('breadcrumbs.userAddress') },
    { label: t('breadcrumbs.edit') }
  ];

  return (
    <PageLayout
      title={t('editAddress')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}

    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-md-6">
            {hasPermission("Change User Country") ? (
              <>
                <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
                  {t('fields.country')}
                </label>
                <select
                  className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
                  name="countryId"
                  value={formData.countryId || ""}
                  onChange={handleInputChange}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <option value="">{t('placeholders.country')}</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
                  {t('fields.country')}
                </label>
                <div className={`form-control-plaintext ${isRTL ? 'text-end' : 'text-start'}`}>
                  {formData.country || 'N/A'}
                </div>
              </>
            )}
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.city')}
            </label>
            <select
              className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
              name="cityId"
              value={formData.cityId || ""}
              onChange={handleInputChange}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('placeholders.city')}</option>
              {cities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.address')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder={t('placeholders.address')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.address2')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="address2"
              value={formData.address2}
              onChange={handleInputChange}
              placeholder={t('placeholders.address2')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton
            variant="primary"
            onClick={submitItem}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('actions.updating') : t('actions.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
